import React, { useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { GroupedRawIngredient } from '../../store/selectors/rawIngredientsSelectors';
import { RawIngredientGroupRow } from './RawIngredientGroupRow';
import { RawIngredientTraceCodeRow } from './RawIngredientTraceCodeRow';
import { updateRawIngredientAsync, deleteRawIngredientAsync } from '../../store/slices/rawIngredientsSlice';
import { RawIngredient } from '../../types/rawIngredient';

interface RawIngredientsTableProps {
  ingredients: GroupedRawIngredient[];
  loading: boolean;
  error: string | null;
}

export const RawIngredientsTable: React.FC<RawIngredientsTableProps> = ({
  ingredients,
  loading,
  error
}) => {
  const dispatch = useAppDispatch();
  const rawIngredients = useAppSelector(state => state.rawIngredients.ingredients);

  // State for expandable rows
  const [expandedIngredients, setExpandedIngredients] = useState<Set<string>>(new Set());

  // State for editing
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState('');
  const [editingItemUnitSize, setEditingItemUnitSize] = useState('');
  const [editingItemQuantity, setEditingItemQuantity] = useState(0);

  // Create a map for quick raw ingredient lookups
  const rawIngredientMap = useMemo(() => {
    return new Map(rawIngredients.map(ing => [ing.id, ing]));
  }, [rawIngredients]);

  const toggleIngredient = (name: string) => {
    const newExpanded = new Set(expandedIngredients);
    if (newExpanded.has(name)) {
      newExpanded.delete(name);
    } else {
      newExpanded.add(name);
    }
    setExpandedIngredients(newExpanded);
  };

  const handleStartEditingItem = (item: RawIngredient) => {
    setEditingItemId(item.id);
    setEditingItemName(item.name);
    setEditingItemUnitSize(item.unitSize || '');
    setEditingItemQuantity(item.quantity);
  };

  const handleSaveRawIngredient = (item: RawIngredient) => {
    if (editingItemName.trim()) {
      dispatch(
        updateRawIngredientAsync({
          ...item,
          name: editingItemName,
          unitSize: editingItemUnitSize,
          quantity: editingItemQuantity,
          receivingLogId: item.receivingLogId || '',
          createdAt: item.createdAt,
          updatedAt: new Date().toISOString()
        })
      );
      setEditingItemId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
  };

  const handleDeleteRawIngredient = (id: string, name: string, traceCode: string) => {
    const confirmMessage = `Are you sure you want to delete "${name}" with trace code ${traceCode}?`;
    if (window.confirm(confirmMessage)) {
      dispatch(deleteRawIngredientAsync(id));
    }
  };

  if (loading) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: '#999',
          fontSize: '15px'
        }}
      >
        Loading raw ingredients...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}
      >
        Error loading raw ingredients: {error}
      </div>
    );
  }

  if (ingredients.length === 0) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: '#999',
          fontSize: '15px'
        }}
      >
        No raw ingredients found.
      </div>
    );
  }

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}
    >
      <thead>
        <tr
          style={{
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #e0e0e0'
          }}
        >
          <th
            style={{
              padding: '16px',
              textAlign: 'left',
              fontWeight: '600',
              color: '#555',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Item Name
          </th>
          <th
            style={{
              padding: '16px',
              textAlign: 'left',
              fontWeight: '600',
              color: '#555',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '300px'
            }}
          >
            Trace Code / Lots
          </th>
          <th
            style={{
              padding: '16px',
              textAlign: 'left',
              fontWeight: '600',
              color: '#555',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '180px'
            }}
          >
            Unit Size
          </th>
          <th
            style={{
              padding: '16px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#555',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '150px'
            }}
          >
            Quantity
          </th>
          <th
            style={{
              padding: '16px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#555',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '150px'
            }}
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {ingredients.map(group => (
          <React.Fragment key={group.ingredientName}>
            <RawIngredientGroupRow
              group={group}
              isExpanded={expandedIngredients.has(group.ingredientName)}
              onToggle={() => toggleIngredient(group.ingredientName)}
            />
            {expandedIngredients.has(group.ingredientName) &&
              group.traceCodes.map(tc => {
                const rawIngredient = rawIngredientMap.get(tc.rawIngredientId);
                if (!rawIngredient) return null;

                return (
                  <RawIngredientTraceCodeRow
                    key={tc.id}
                    traceCode={tc}
                    rawIngredient={rawIngredient}
                    isEditing={editingItemId === rawIngredient.id}
                    editingName={editingItemName}
                    editingUnitSize={editingItemUnitSize}
                    editingQuantity={editingItemQuantity}
                    onStartEdit={handleStartEditingItem}
                    onSave={handleSaveRawIngredient}
                    onCancel={handleCancelEdit}
                    onDelete={handleDeleteRawIngredient}
                    onNameChange={setEditingItemName}
                    onUnitSizeChange={setEditingItemUnitSize}
                    onQuantityChange={setEditingItemQuantity}
                  />
                );
              })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};
